import React, { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Video, VideoOff, MonitorUp, PhoneOff, Loader2, AlertTriangle, Presentation, Eye, Circle } from 'lucide-react';
import { Button } from './ui/button';
import { SignalingClient } from '../lib/webrtc/SignalingClient';
import { PeerConnection } from '../lib/webrtc/PeerConnection';
import { TranscriptRecorder } from '../lib/proposal/transcriptRecorder';
import ProposalPresentation from './proposal/ProposalPresentation';
import ProposalViewer from './proposal/ProposalViewer';
import DraggablePiP from './DraggablePiP';
import ParticipantCameraStrip from './ParticipantCameraStrip';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * MeetingRoom — generic 1-on-1 video room.
 *
 * Used by:
 *   • BrokerMeetingPage  (role="host", token = host_token)
 *   • PublicJoinPage     (role="guest", token = guest_token)
 *
 * Lifecycle:
 *   1. Fetch ICE config from /api/public/meetings/:room/turn-credentials
 *   2. Open WS to /api/ws/meeting/:room
 *   3. Create RTCPeerConnection, attach local media
 *   4. Host sends offer when WS reports `ready` (both peers connected)
 *   5. Relay all SDP/ICE through the WS
 */
const MeetingRoom = ({ roomId, token, role, hostName, clientName, proposalId, authToken, meetingId, onLeave }) => {
  const localRef = useRef(null);
  // Map: peer_id -> { peer: PeerConnection, role, name, videoEl: HTMLVideoElement | null,
  //                   stream: MediaStream | null, iceType: {local, remote} | null }
  const peersRef = useRef(new Map());
  const localStreamRef = useRef(null);
  // Single display-capture stream shared across every peer when screen-share
  // is active (multi-guest support). Hoisted to MeetingRoom so we acquire it
  // once and fan-out across every PeerConnection.
  const screenStreamRef = useRef(null);
  const sigRef = useRef(null);
  const transcribeRef = useRef(null);
  const selfPeerIdRef = useRef(null);
  const iceServersRef = useRef([{ urls: 'stun:stun.l.google.com:19302' }]);

  const [status, setStatus] = useState('connecting'); // connecting | waiting | live | failed | ended
  const [errorMsg, setErrorMsg] = useState(null);
  const [audioOn, setAudioOn] = useState(true);
  const [videoOn, setVideoOn] = useState(true);
  const [screenOn, setScreenOn] = useState(false);
  // Array of peers other than self, used for UI rendering.
  // Each entry: { peer_id, role, name, hasStream, iceType }
  const [remotePeers, setRemotePeers] = useState([]);
  const [reconnecting, setReconnecting] = useState(false);

  // ── Proposal sync state (used by both broker presentation + guest viewer) ──
  const [presentingId, setPresentingId] = useState(proposalId || null);
  const [presenting, setPresenting] = useState(false); // broker side: panel open
  const [guestProposal, setGuestProposal] = useState(null); // {type, payload, slideIdx, accepted}
  const [acceptedFlag, setAcceptedFlag] = useState(false);

  // ── Transcription (host-only feature; broker decides to record) ──
  const [transcribing, setTranscribing] = useState(false);
  const [transcriptCount, setTranscriptCount] = useState(0);
  const [remoteRecording, setRemoteRecording] = useState(false);

  // ── Client viewing indicator (broker sees which slide guest is on) ──
  // {slideIdx, since: timestamp ms}
  const [clientViewing, setClientViewing] = useState(null);
  const [, _force] = useState(0); // trigger re-render every 5s for "client paused N:NN"
  useEffect(() => {
    if (role !== 'host') return;
    const t = setInterval(() => _force((x) => x + 1), 5_000);
    return () => clearInterval(t);
  }, [role]);

  // ── Derived flags from remotePeers ──
  const peerPresent = remotePeers.length > 0;
  // Highest-priority iceType across peers (relay wins for display)
  const iceType = remotePeers.reduce((acc, p) => {
    if (!p.iceType) return acc;
    if (!acc) return p.iceType;
    if (p.iceType.local === 'relay' || p.iceType.remote === 'relay') return p.iceType;
    return acc;
  }, null);

  // ── Helper: refresh the public remotePeers state from peersRef ──
  const syncRemotePeers = () => {
    const list = [];
    peersRef.current.forEach((entry, peerId) => {
      if (peerId === selfPeerIdRef.current) return;
      list.push({
        peer_id: peerId,
        role: entry.role,
        name: entry.name,
        hasStream: !!entry.stream,
        iceType: entry.iceType,
      });
    });
    setRemotePeers(list);
  };

  // ── Helper: build a fresh PeerConnection for a given remote peer ──
  // shouldOffer = true when *this* side will create the SDP offer
  //   (host sends offers to guests; guests answer the host's offer).
  const createPeerFor = (remotePeerId, remoteRole, remoteName, { shouldOffer }) => {
    if (peersRef.current.has(remotePeerId)) return peersRef.current.get(remotePeerId);

    const peer = new PeerConnection({
      iceServers: iceServersRef.current,
      isOfferer: shouldOffer,
      onSignal: (sig) => {
        // Route every signaling message to *this* peer via 'to'.
        sigRef.current?.send(sig.type, sig.payload, { to: remotePeerId });
      },
      onRemoteStream: (stream) => {
        const entry = peersRef.current.get(remotePeerId);
        if (!entry) return;
        entry.stream = stream;
        // Attach to the video element if we already mounted it
        if (entry.videoEl) {
          entry.videoEl.srcObject = stream;
          const pp = entry.videoEl.play?.();
          if (pp && typeof pp.catch === 'function') pp.catch(() => {});
        }
        syncRemotePeers();
      },
      onConnectionState: (s) => {
        if (s === 'connected') {
          setStatus('live');
          setReconnecting(false);
        } else if (s === 'failed') {
          // Auto-recover for the offerer side.
          setReconnecting(true);
          if (shouldOffer) {
            setTimeout(() => {
              peersRef.current.get(remotePeerId)?.peer.restartIce().catch(() => {});
            }, 500);
          }
        } else if (s === 'disconnected') {
          setReconnecting(true);
        }
      },
    });
    peer._onIceTypeKnown = ({ local, remote }) => {
      const entry = peersRef.current.get(remotePeerId);
      if (!entry) return;
      entry.iceType = { local, remote };
      syncRemotePeers();
    };
    // Reuse the existing local stream so both peers see broker's camera.
    if (localStreamRef.current) {
      peer.attachLocalStream(localStreamRef.current);
    }
    // If a screen-share is already active when this peer is created (e.g. a
    // new guest joins mid-share), attach the existing capture so they see
    // the screen immediately.
    if (screenStreamRef.current) {
      peer.attachScreenStream(screenStreamRef.current);
    }
    peersRef.current.set(remotePeerId, {
      peer,
      role: remoteRole,
      name: remoteName || '',
      videoEl: null,
      stream: null,
      iceType: null,
    });
    return peersRef.current.get(remotePeerId);
  };

  const destroyPeerFor = (remotePeerId) => {
    const entry = peersRef.current.get(remotePeerId);
    if (!entry) return;
    try { entry.peer.close(); } catch (e) { /* */ }
    if (entry.videoEl) entry.videoEl.srcObject = null;
    peersRef.current.delete(remotePeerId);
    syncRemotePeers();
  };

  useEffect(() => {
    let cancelled = false;
    const cleanup = () => {
      peersRef.current.forEach((entry) => {
        try { entry.peer.close(); } catch (e) { /* */ }
      });
      peersRef.current.clear();
      try { localStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) { /* */ }
      localStreamRef.current = null;
      try { screenStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) { /* */ }
      screenStreamRef.current = null;
      try { sigRef.current?.close(); } catch (e) { /* */ }
    };

    const setup = async () => {
      // 1. ICE config — shared across every PC we'll create.
      let iceServers = [{ urls: 'stun:stun.l.google.com:19302' }];
      try {
        const r = await fetch(`${BACKEND}/api/public/meetings/${roomId}/turn-credentials?token=${encodeURIComponent(token)}`);
        if (r.ok) {
          const data = await r.json();
          iceServers = data.iceServers || iceServers;
        }
      } catch (e) {
        // continue with STUN only
      }
      if (cancelled) return;
      iceServersRef.current = iceServers;

      // 2. Acquire local media ONCE — all PCs share these tracks.
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          video: { width: { ideal: 1280, max: 1920 }, height: { ideal: 720, max: 1080 }, facingMode: 'user' },
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        localStreamRef.current = stream;
        if (localRef.current) {
          localRef.current.srcObject = stream;
          const p = localRef.current.play?.();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        }
      } catch (err) {
        setStatus('failed');
        setErrorMsg('Camera or microphone permission denied. Please allow access and reload.');
        return;
      }

      // 3. Signaling
      const sig = new SignalingClient({
        roomId,
        token,
        name: role === 'host' ? hostName : clientName,
        onOpen: () => {
          setStatus('waiting');
          setReconnecting(false);
        },
        onReconnect: async () => {
          // After a network drop + WS reconnect, kick an ICE-restart on
          // every peer we own as the offerer.
          peersRef.current.forEach((entry) => {
            if (entry.peer.isOfferer) {
              entry.peer.restartIce().catch(() => {});
            }
          });
          setReconnecting(false);
        },
        onClose: (e) => {
          if (e && e.reason === 'reconnect-exhausted') {
            setStatus('failed');
            setErrorMsg('Connection lost — please refresh to reconnect.');
            return;
          }
          // If no peer is connected, surface 'ended'.
          const anyConnected = Array.from(peersRef.current.values())
            .some((entry) => entry.peer.pc?.connectionState === 'connected');
          if (!anyConnected) setStatus('ended');
          setReconnecting(true);
        },
        onError: () => {
          setReconnecting(true);
        },
        onMessage: async (msg) => {
          switch (msg.kind) {
            case 'hello':
              selfPeerIdRef.current = msg.you;
              // For each existing peer, decide who initiates.
              //   host → guest:  host initiates (shouldOffer=true for host's PCs).
              //   guest → host:  guest waits for offer (shouldOffer=false on guest's side).
              (msg.peers || []).forEach((p) => {
                const iAmHost = msg.role === 'host';
                createPeerFor(p.peer_id, p.role, p.name, { shouldOffer: iAmHost });
              });
              syncRemotePeers();
              break;
            case 'peer-joined': {
              const iAmHost = role === 'host';
              // If I'm the host and a guest just joined, I'll initiate.
              // If I'm a guest and the host (re)joined, host will initiate
              // — but we still create the PC ahead of time so the offer
              // has a place to land.
              const shouldOffer = iAmHost && msg.role === 'guest';
              createPeerFor(msg.peer_id, msg.role, msg.name, { shouldOffer });
              syncRemotePeers();
              break;
            }
            case 'peer-left':
              destroyPeerFor(msg.peer_id);
              setClientViewing(null);
              if (peersRef.current.size === 0) setStatus('waiting');
              break;
            case 'ready':
              if (role === 'host' && msg.peer_id) {
                const entry = peersRef.current.get(msg.peer_id);
                if (entry) {
                  try { await entry.peer.start(); } catch (e) { /* */ }
                }
              }
              break;
            case 'offer': {
              const from = msg.from;
              if (!from) break;
              let entry = peersRef.current.get(from);
              if (!entry) {
                // Defensive: a peer-joined could have been missed (e.g. on a
                // reconnect). Create the PC on demand as the answerer.
                entry = createPeerFor(from, msg.fromRole || 'host', msg.fromName || '', { shouldOffer: false });
              }
              await entry.peer.handleOffer(msg.payload);
              break;
            }
            case 'answer': {
              const from = msg.from;
              if (!from) break;
              const entry = peersRef.current.get(from);
              if (entry) await entry.peer.handleAnswer(msg.payload);
              break;
            }
            case 'ice': {
              const from = msg.from;
              if (!from) break;
              const entry = peersRef.current.get(from);
              if (entry) await entry.peer.handleIce(msg.payload);
              break;
            }
            case 'app': {
              // Proposal sync from broker → guest, or accept from guest → broker
              const p = msg.payload || {};
              if (role === 'guest') {
                if (p.type === 'value:update') {
                  setGuestProposal((prev) => ({
                    ...(prev || {}),
                    type: p.proposalType || prev?.type || 'consolidation',
                    payload: p.payload,
                    slideIdx: prev?.slideIdx || 0,
                  }));
                } else if (p.type === 'slide:goto') {
                  setGuestProposal((prev) => ({
                    ...(prev || { type: 'consolidation', payload: {} }),
                    slideIdx: p.slide,
                  }));
                } else if (p.type === 'accept') {
                  // broker confirmed acceptance
                  setAcceptedFlag(true);
                  setGuestProposal((prev) => ({ ...(prev || { type: 'consolidation', payload: {}, slideIdx: 0 }), accepted: true }));
                }
              } else if (role === 'host') {
                if (p.type === 'accept') {
                  setAcceptedFlag(true);
                }
                if (p.type === 'slide:view') {
                  setClientViewing((prev) => {
                    if (prev && prev.slideIdx !== p.slide && meetingId) {
                      const dwellSecs = (Date.now() - prev.since) / 1000;
                      try {
                        const fd = new FormData();
                        fd.append('token', token);
                        fd.append('slide_idx', String(prev.slideIdx));
                        fd.append('secs', String(dwellSecs));
                        fetch(`${BACKEND}/api/meetings/${meetingId}/slide-view`, { method: 'POST', body: fd }).catch(() => {});
                      } catch (e) { /* */ }
                    }
                    return { slideIdx: p.slide, since: Date.now() };
                  });
                }
              }
              if (p.type === 'transcribe:state') {
                setRemoteRecording(!!p.on);
                if (role === 'guest' && meetingId && TranscriptRecorder.isSupported()) {
                  if (p.on && !transcribeRef.current) {
                    const stream = localStreamRef.current;
                    if (stream) {
                      const rec = new TranscriptRecorder({
                        stream,
                        meetingId,
                        token,
                        speakerName: clientName,
                        backendUrl: BACKEND,
                        onError: (e) => console.warn('Guest transcribe', e),
                      });
                      rec.start();
                      transcribeRef.current = rec;
                      setTranscribing(true);
                    }
                  } else if (!p.on && transcribeRef.current) {
                    try { transcribeRef.current.stop(); } catch (e) { /* */ }
                    transcribeRef.current = null;
                    setTranscribing(false);
                  }
                }
              }
              break;
            }
            default:
              break;
          }
        },
      });
      sigRef.current = sig;
      sig.connect();
    };

    setup();
    return () => {
      cancelled = true;
      cleanup();
    };
    // eslint-disable-next-line
  }, [roomId, token, role]);

  // Emit slide:view from guest → host whenever the guest's mirror slide changes
  // so the broker can see exactly which slide the client is looking at.
  useEffect(() => {
    if (role !== 'guest') return;
    if (!guestProposal || guestProposal.slideIdx == null) return;
    if (!sigRef.current) return;
    sigRef.current.send('app', { type: 'slide:view', slide: guestProposal.slideIdx });
    // We only want this to fire when the slide index actually changes, not on every render.
  }, [role, guestProposal?.slideIdx]);

  // iOS Safari aggressively suspends WebRTC + WebSocket when the app goes
  // to background (lock screen / app switch). When we come back to the
  // foreground, kick the host's ICE-restart so media resumes — and re-issue
  // play() on the video tags since iOS can pause them silently.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      // Re-trigger play on the local + remote video tags (iOS pauses them
      // silently when backgrounded; autoPlay alone won't recover).
      const els = [localRef.current];
      peersRef.current.forEach((entry) => { if (entry.videoEl) els.push(entry.videoEl); });
      els.forEach((v) => {
        if (!v) return;
        const p = v.play?.();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      });
      // Host nudges ICE on every peer if media stopped flowing.
      if (role === 'host') {
        peersRef.current.forEach((entry) => {
          if (entry.peer.pc?.connectionState !== 'connected') {
            entry.peer.restartIce().catch(() => {});
          }
        });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [role]);

  // ── Connection-failure timeout — if media doesn't connect within 30s
  // after WS confirms the peer joined, show a hard error banner with
  // actionable next-steps. Without this the user just sees "Connecting
  // media…" forever when TURN fails. ──
  useEffect(() => {
    if (!peerPresent || status === 'live') return;
    const timer = setTimeout(() => {
      // Still not live after 30s with the peer present? Something's broken.
      if (status !== 'live') {
        setStatus('failed');
        setErrorMsg('Could not establish a media connection. This usually means a TURN relay is needed but unavailable. Please contact support if the problem persists.');
      }
    }, 30_000);
    return () => clearTimeout(timer);
  }, [peerPresent, status]);

  const onToggleAudio = () => {
    const next = !audioOn;
    // Toggling the source tracks once affects every PC since they all share
    // the same MediaStream.
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = next; });
    }
    setAudioOn(next);
  };
  const onToggleVideo = () => {
    const next = !videoOn;
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach((t) => { t.enabled = next; });
    }
    setVideoOn(next);
  };
  const onToggleScreen = async () => {
    // Multi-guest screen share: acquire one displayMedia capture and fan it
    // out to every active PeerConnection. The negotiationneeded handler on
    // each PC takes care of SDP renegotiation automatically.
    if (peersRef.current.size === 0) {
      // No-one to share with yet. Bail out — the user can re-toggle once
      // a guest joins.
      return;
    }
    if (screenOn) {
      // Stop sharing everywhere.
      peersRef.current.forEach((entry) => {
        try { entry.peer.detachScreenStream(); } catch (e) { /* */ }
      });
      try { screenStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) { /* */ }
      screenStreamRef.current = null;
      setScreenOn(false);
      return;
    }
    // Acquire the display capture once.
    let display;
    try {
      display = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    } catch (e) {
      // user cancelled the picker
      return;
    }
    screenStreamRef.current = display;
    // When the user clicks the browser's native "Stop sharing" pill, mirror
    // that into our state so the on-screen toggle stays accurate.
    display.getVideoTracks().forEach((t) => {
      t.addEventListener('ended', () => {
        // Best-effort cleanup; the toggle handler above is idempotent.
        peersRef.current.forEach((entry) => {
          try { entry.peer.detachScreenStream(); } catch (err) { /* */ }
        });
        screenStreamRef.current = null;
        setScreenOn(false);
      });
    });
    // Fan-out: attach to every peer. negotiationneeded fires on each PC
    // (only the offerer-side PCs actually emit a new offer; the polite peer
    // accepts via handleOffer).
    peersRef.current.forEach((entry) => {
      try { entry.peer.attachScreenStream(display); } catch (e) { /* */ }
    });
    setScreenOn(true);
  };
  const onLeaveClick = () => {
    // Flush the final slide-dwell so analytics aren't truncated on leave.
    if (role === 'host' && clientViewing && meetingId) {
      const dwellSecs = (Date.now() - clientViewing.since) / 1000;
      try {
        const fd = new FormData();
        fd.append('token', token);
        fd.append('slide_idx', String(clientViewing.slideIdx));
        fd.append('secs', String(dwellSecs));
        // Use sendBeacon if available so the request survives navigation.
        if (typeof navigator.sendBeacon === 'function') {
          navigator.sendBeacon(`${BACKEND}/api/meetings/${meetingId}/slide-view`, fd);
        } else {
          fetch(`${BACKEND}/api/meetings/${meetingId}/slide-view`, { method: 'POST', body: fd, keepalive: true }).catch(() => {});
        }
      } catch (e) { /* */ }
    }
    try { transcribeRef.current?.stop(); } catch (e) { /* */ }
    peersRef.current.forEach((entry) => {
      try { entry.peer.close(); } catch (e) { /* */ }
    });
    peersRef.current.clear();
    try { localStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) { /* */ }
    localStreamRef.current = null;
    try { screenStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (e) { /* */ }
    screenStreamRef.current = null;
    try { sigRef.current?.close(); } catch (e) { /* */ }
    if (onLeave) onLeave();
  };

  // Toggle transcript recording (host only). Records the LOCAL mic — both
  // peers do this independently if they opt in, then the backend stitches
  // both sides into a single chronological transcript.
  const onToggleTranscribe = () => {
    if (transcribing) {
      try { transcribeRef.current?.stop(); } catch (e) { /* */ }
      transcribeRef.current = null;
      setTranscribing(false);
      // Tell the other peer the recording stopped (so the indicator clears
      // on their side).
      sigRef.current?.send('app', { type: 'transcribe:state', on: false });
      return;
    }
    const stream = localStreamRef.current;
    if (!stream) return;
    if (!TranscriptRecorder.isSupported()) return;
    if (!meetingId) return;
    const rec = new TranscriptRecorder({
      stream,
      meetingId,
      token,
      speakerName: role === 'host' ? hostName : clientName,
      backendUrl: BACKEND,
      onSegment: () => setTranscriptCount((c) => c + 1),
      onError: (e) => console.warn('Transcribe', e),
    });
    rec.start();
    transcribeRef.current = rec;
    setTranscribing(true);
    sigRef.current?.send('app', { type: 'transcribe:state', on: true });
  };

  const statusBanner = () => {
    if (status === 'failed') {
      return (
        <div className="absolute inset-x-0 top-0 bg-rose-600 text-white px-4 py-2 text-sm flex items-center gap-2 z-50" data-testid="meeting-status-banner">
          <AlertTriangle className="h-4 w-4" /> {errorMsg || 'Connection failed'}
        </div>
      );
    }
    if (status === 'ended') {
      return (
        <div className="absolute inset-x-0 top-0 bg-slate-700 text-white px-4 py-2 text-sm z-50" data-testid="meeting-status-banner">
          The meeting has ended.
        </div>
      );
    }
    if (reconnecting && status !== 'failed') {
      return (
        <div className="absolute inset-x-0 top-0 bg-amber-500 text-slate-950 px-4 py-2 text-sm flex items-center gap-2 backdrop-blur z-50" data-testid="meeting-reconnecting-banner">
          <Loader2 className="h-4 w-4 animate-spin" /> Reconnecting…
        </div>
      );
    }
    if (status === 'waiting' || (status === 'connecting' && !peerPresent)) {
      // Differentiate "peer hasn't joined the room" vs "peer joined but
      // ICE/media still negotiating". Both used to show the same banner —
      // misleading when TURN fails because brokers think the client never
      // joined when actually it's a media-flow issue.
      const peerWaiting = !peerPresent;
      const peerName = role === 'host' ? (clientName || 'your client') : (hostName || 'the broker');
      const message = peerWaiting
        ? (role === 'host' ? `Waiting for ${peerName} to join…` : `Waiting for ${peerName} to start the meeting…`)
        : `Connecting media with ${peerName}…`;
      return (
        <div className="absolute inset-x-0 top-0 bg-slate-900/80 text-white px-4 py-2 text-sm flex items-center gap-2 backdrop-blur z-50" data-testid="meeting-status-banner">
          <Loader2 className="h-4 w-4 animate-spin" /> {message}
        </div>
      );
    }
    return null;
  };

  // Stamp on top of slide nav showing the client's current slide focus.
  const clientViewingBadge = () => {
    if (role !== 'host') return null;
    if (!clientViewing) return null;
    const ms = Date.now() - clientViewing.since;
    const secs = Math.floor(ms / 1000);
    const mins = Math.floor(secs / 60);
    const lingerStr = mins > 0
      ? `${mins}:${String(secs % 60).padStart(2, '0')} on this slide`
      : `${secs}s on this slide`;
    return (
      <div
        className="absolute top-3 left-3 z-40 inline-flex items-center gap-2 bg-slate-950/85 backdrop-blur border border-emerald-500/40 text-white text-xs px-3 py-1.5 rounded-full shadow-lg"
        data-testid="client-viewing-badge"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        <Eye className="h-3.5 w-3.5 text-emerald-400" />
        <span className="font-semibold">Client viewing slide {clientViewing.slideIdx + 1}</span>
        <span className="text-slate-400">·</span>
        <span className="text-slate-300">{lingerStr}</span>
      </div>
    );
  };

  const recordingIndicator = () => {
    const recording = transcribing || remoteRecording;
    if (!recording) return null;
    return (
      <div
        className="absolute top-3 right-3 z-40 inline-flex items-center gap-2 bg-rose-600/95 backdrop-blur text-white text-xs px-3 py-1.5 rounded-full shadow-lg"
        data-testid="recording-indicator"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-rose-300 opacity-75 animate-ping" />
          <Circle className="h-2 w-2 fill-white text-white" />
        </span>
        <span className="font-semibold uppercase tracking-wide">REC</span>
        {transcribing && transcriptCount > 0 && (
          <span className="text-rose-100">{transcriptCount} segment{transcriptCount === 1 ? '' : 's'}</span>
        )}
      </div>
    );
  };

  // Tiny ICE-path diagnostic at the bottom-right above the controls bar.
  // Only renders once the connection state is 'live' so it doesn't add
  // noise during connection setup. Useful for the broker to spot when the
  // call is going via a TURN relay (slightly higher latency, but the call
  // is working) vs direct P2P.
  const iceDiagnostic = () => {
    if (status !== 'live' || !iceType) return null;
    const isRelay = iceType.local === 'relay' || iceType.remote === 'relay';
    return (
      <div
        className={`absolute bottom-24 left-3 z-30 inline-flex items-center gap-1.5 backdrop-blur text-[10px] uppercase tracking-wide font-semibold px-2 py-1 rounded ${
          isRelay
            ? 'bg-amber-500/90 text-slate-950'
            : 'bg-emerald-600/90 text-white'
        }`}
        data-testid="ice-diagnostic"
        title={isRelay ? 'Media is flowing via a TURN relay' : 'Direct peer-to-peer connection'}
      >
        <span className="relative flex h-1.5 w-1.5">
          <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping ${isRelay ? 'bg-amber-300' : 'bg-emerald-300'}`} />
          <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${isRelay ? 'bg-amber-200' : 'bg-emerald-200'}`} />
        </span>
        {isRelay ? 'Via TURN relay' : 'Direct P2P'}
      </div>
    );
  };

  return (
    <div className="relative w-full h-screen bg-slate-950 text-white overflow-hidden" data-testid="meeting-room">
      {statusBanner()}
      {recordingIndicator()}
      {clientViewingBadge()}
      {iceDiagnostic()}

      {/* Remote video tiles — one per other peer in the room. Layout:
            0 peers → empty / "waiting" overlay below
            1 peer  → full-bleed (broker-1-client classic view)
            2 peers → vertical split
            3 peers → 2x2 grid with one cell empty (host top-left visually)
          Each tile self-attaches its srcObject via a ref callback so we
          don't fight React reconciliation on stream identity. */}
      {remotePeers.length > 0 && (
        <div
          className={`absolute inset-0 grid gap-1 bg-slate-900 ${
            remotePeers.length === 1
              ? 'grid-cols-1'
              : remotePeers.length === 2
                ? 'grid-cols-1 md:grid-cols-2'
                : 'grid-cols-2'
          }`}
          data-testid="remote-video-grid"
        >
          {remotePeers.map((p) => (
            <div key={p.peer_id} className="relative w-full h-full bg-slate-950 overflow-hidden">
              <video
                autoPlay
                playsInline
                className="absolute inset-0 w-full h-full object-cover bg-slate-900"
                data-testid="remote-video"
                ref={(el) => {
                  const entry = peersRef.current.get(p.peer_id);
                  if (!entry) return;
                  entry.videoEl = el;
                  if (el && entry.stream && el.srcObject !== entry.stream) {
                    el.srcObject = entry.stream;
                    const pp = el.play?.();
                    if (pp && typeof pp.catch === 'function') pp.catch(() => {});
                  }
                }}
              />
              {/* Per-tile name label */}
              {(p.name || p.role) && (
                <div className="absolute bottom-2 left-2 bg-slate-900/70 backdrop-blur text-white text-xs px-2 py-1 rounded-md font-medium" data-testid={`peer-name-${p.peer_id}`}>
                  {p.name || (p.role === 'host' ? 'Broker' : 'Guest')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {!peerPresent && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <div className="w-24 h-24 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4">
              <Loader2 className="h-10 w-10 animate-spin text-emerald-400" />
            </div>
            <p className="text-slate-300 text-sm">
              {role === 'host'
                ? `Waiting for ${clientName || 'your client'}…`
                : `Waiting for ${hostName || 'the broker'}…`}
            </p>
          </div>
        </div>
      )}

      {/* Local video — DRAGGABLE picture-in-picture. The broker can park
          this anywhere on screen so it doesn't cover proposal content. */}
      <DraggablePiP testId="local-video-pip" size={{ w: 224, h: 126 }}>
        <video
          ref={localRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full rounded-lg shadow-xl border border-white/10 bg-slate-800 object-cover pointer-events-none"
          data-testid="local-video"
        />
      </DraggablePiP>

      {/* Broker presentation overlay (drives the proposal sync) */}
      {role === 'host' && presenting && presentingId && (
        <ProposalPresentation
          proposalId={presentingId}
          authToken={authToken}
          signalingSend={(kind, payload) => sigRef.current?.send(kind, payload)}
          clientViewing={clientViewing}
          onClose={() => setPresenting(false)}
        />
      )}

      {/* Guest mirrored proposal viewer (no broker controls — read-only) */}
      {role === 'guest' && guestProposal && guestProposal.payload && (
        <ProposalViewer
          type={guestProposal.type}
          payload={guestProposal.payload}
          slideIdx={guestProposal.slideIdx || 0}
          clientName={clientName}
          hostName={hostName}
          accepted={acceptedFlag || guestProposal.accepted}
          onAccept={() => {
            setAcceptedFlag(true);
            sigRef.current?.send('app', { type: 'accept' });
            // Public accept is also recorded on the server via revisit token (Phase 2.x)
          }}
        />
      )}

      {/* Camera strip — visible whenever a proposal is being presented so
          every participant can still see each other's faces over the deck.
          The broker's own face is already shown via the DraggablePiP above;
          this strip shows the OTHER participants. */}
      {((role === 'host' && presenting && presentingId) ||
        (role === 'guest' && guestProposal && guestProposal.payload)) &&
        remotePeers.length > 0 && (
          <ParticipantCameraStrip peersRef={peersRef} remotePeers={remotePeers} />
        )}

      {/* Bottom controls */}
      <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-slate-950 via-slate-950/90 to-transparent">
        <div className="flex items-center justify-center gap-3">
          <ControlBtn onClick={onToggleAudio} active={audioOn} testId="ctrl-audio">
            {audioOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
          </ControlBtn>
          <ControlBtn onClick={onToggleVideo} active={videoOn} testId="ctrl-video">
            {videoOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
          </ControlBtn>
          {role === 'host' && (
            <ControlBtn
              onClick={onToggleScreen}
              active={screenOn}
              testId="ctrl-screen"
              emerald={screenOn}
              disabled={peersRef.current.size === 0}
              title={
                peersRef.current.size === 0
                  ? 'Waiting for a guest to join…'
                  : screenOn
                    ? `Stop sharing screen (${peersRef.current.size} guest${peersRef.current.size === 1 ? '' : 's'} watching)`
                    : `Share your screen with ${peersRef.current.size} guest${peersRef.current.size === 1 ? '' : 's'}`
              }
            >
              <MonitorUp className="h-5 w-5" />
            </ControlBtn>
          )}
          {role === 'host' && presentingId && (
            <ControlBtn onClick={() => setPresenting((p) => !p)} active={presenting} testId="ctrl-present" emerald={presenting}>
              <Presentation className="h-5 w-5" />
            </ControlBtn>
          )}
          {role === 'host' && meetingId && TranscriptRecorder.isSupported() && (
            <ControlBtn
              onClick={onToggleTranscribe}
              active={!transcribing}
              testId="ctrl-transcribe"
              emerald={false}
            >
              <Circle className={`h-5 w-5 ${transcribing ? 'fill-rose-500 text-rose-500' : ''}`} />
            </ControlBtn>
          )}
          <Button
            onClick={onLeaveClick}
            data-testid="ctrl-leave"
            className="bg-rose-600 hover:bg-rose-700 text-white rounded-full h-12 px-6"
          >
            <PhoneOff className="h-5 w-5 mr-2" /> Leave
          </Button>
        </div>
      </div>
    </div>
  );
};

const ControlBtn = ({ children, onClick, active = true, testId, emerald = false, title, disabled = false }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    title={title}
    disabled={disabled}
    className={`h-12 w-12 rounded-full flex items-center justify-center transition-colors border ${
      disabled
        ? 'bg-slate-800/40 border-white/5 text-white/30 cursor-not-allowed'
        : emerald
          ? 'bg-emerald-600 hover:bg-emerald-700 border-emerald-700 text-white'
          : active
            ? 'bg-slate-800/80 hover:bg-slate-700 border-white/10 text-white'
            : 'bg-rose-600 hover:bg-rose-700 border-rose-700 text-white'
    }`}
  >
    {children}
  </button>
);

export default MeetingRoom;
